import { analyticsApi, type AnalyticsRangeSelection } from '@shared/api-client';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, MessageCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { HelpTooltip } from '@/components/ui/help-tooltip';
import { formatRelative } from '@/lib/utils';

const source = {
  hacker_news: { short: 'HN', label: 'Hacker News' },
  reddit: { short: 'R', label: 'Reddit' },
  linkedin: { short: 'in', label: 'LinkedIn' },
  x: { short: 'X', label: 'X' },
  web: { short: 'W', label: 'Web' },
} as const;

export const ExternalSignals = ({
  projectId,
  range,
}: {
  projectId: string;
  range: AnalyticsRangeSelection;
}) => {
  const query = useQuery({
    queryKey: ['analytics', 'external-signals', projectId, range],
    queryFn: () => analyticsApi.getExternalSignals(projectId, range),
    enabled: Boolean(projectId),
    staleTime: 2 * 60_000,
    refetchInterval: 2 * 60_000,
  });
  const signals = query.data?.signals.slice(0, 3) ?? [];
  if (!signals.length) {
    return null;
  }

  return (
    <section aria-label="External signals">
      <div className="mb-2 flex items-center gap-2 px-1 text-xs font-medium text-ink-muted">
        <MessageCircle className="size-3.5" />
        External signals
        <HelpTooltip label="About external signals">
          Public mentions matching the project name, domain or custom monitoring
          terms. A mention is evidence for investigation, not proof of
          causality.
        </HelpTooltip>
      </div>
      <Card className="divide-y divide-border overflow-hidden shadow-none">
        {signals.map((signal) => (
          <a
            key={signal.id}
            href={signal.url}
            target="_blank"
            rel="noreferrer"
            className="group flex items-center gap-3 px-5 py-3.5 transition-colors hover:bg-surface-muted"
          >
            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-warning/10 text-warning">
              <span className="text-[10px] font-bold">
                {source[signal.source].short}
              </span>
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-ink">
                {signal.title ||
                  signal.body ||
                  `${source[signal.source].label} mention`}
              </div>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-muted">
                <span>{source[signal.source].label}</span>
                <span aria-hidden="true">·</span>
                <span>{formatRelative(signal.publishedAt)}</span>
                <span aria-hidden="true">·</span>
                <span className="truncate">
                  {signal.matchedTerms.join(', ')}
                </span>
              </div>
            </div>
            <ExternalLink className="size-4 shrink-0 text-ink-muted transition-colors group-hover:text-ink" />
          </a>
        ))}
      </Card>
    </section>
  );
};
